/**
 * Puente React ↔ FleetStore.
 *
 * Cada hook de aquí es una suscripción a UNA porción del store vía
 * `useSyncExternalStore`. La regla de uso es simple:
 *
 *   - ¿Pintas un vehículo concreto?  → `useFleetDevice(imei)` / `useFleetTrace(imei)`
 *   - ¿Pintas la lista de la flota?  → `useFleetRoster()` + un hijo por IMEI
 *   - ¿Solo necesitas totales?       → `useFleetCounts()`
 *   - ¿Necesitas el array entero?    → `useFleetDevices()` (ver la advertencia)
 *
 * `useFleetDevices` es el puente para las pantallas que todavía razonan sobre
 * la flota completa. Re-renderiza con CUALQUIER cambio de CUALQUIER vehículo,
 * que es exactamente el comportamiento que la fase 3 vino a quitar del
 * Monitor. Sirve en pantallas que no están abiertas durante el monitoreo
 * continuo (Comandos, Geocercas, Análisis); en el mapa y en la lista lateral
 * no se usa.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createFleetActions } from "../lib/fleet/fleetActions.js";

export const FleetContext = createContext(null);

/** El store crudo. Para lecturas imperativas (fuera de render) y mutaciones. */
export function useFleetStore() {
  const store = useFleetStoreOptional();
  if (!store) {
    throw new Error("useFleetStore debe usarse dentro de <FleetProvider>");
  }
  return store;
}

/**
 * Variante tolerante: devuelve `null` fuera del provider en vez de lanzar.
 * La usan los componentes que también se renderizan en tests aislados.
 */
export function useFleetStoreOptional() {
  return useContext(FleetContext);
}

/**
 * Acciones sobre la flota (`clearSyncRequest`, `notifyQueueOverflow`,
 * `notifyCatchUp`, `refreshDevices`). Identidad estable de por vida: el store
 * no cambia, así que se pueden pasar como props sin romper ningún `React.memo`.
 */
export function useFleetActions() {
  const store = useFleetStore();
  return useMemo(() => createFleetActions(store), [store]);
}

/** Device de un IMEI. Re-renderiza SOLO cuando ese vehículo cambia. */
export function useFleetDevice(imei) {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeDevice(imei, onChange),
    [store, imei],
  );
  const getSnapshot = useCallback(() => store.getDevice(imei), [store, imei]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Traza (newest-first) de un IMEI. Re-renderiza solo con esa traza. */
export function useFleetTrace(imei) {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeTrace(imei, onChange),
    [store, imei],
  );
  const getSnapshot = useCallback(() => store.getTrace(imei), [store, imei]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Petición de sincronización pendiente de un IMEI, o `null`. */
export function useFleetSyncRequest(imei) {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeSyncRequest(imei, onChange),
    [store, imei],
  );
  const getSnapshot = useCallback(
    () => store.getSyncRequest(imei),
    [store, imei],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Último `command_update` recibido para un IMEI, o `null`. */
export function useFleetCommand(imei) {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeCommand(imei, onChange),
    [store, imei],
  );
  const getSnapshot = useCallback(() => store.getCommand(imei), [store, imei]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Mapa `{ [imei]: command }` completo — para los efectos de toasts. */
export function useFleetCommands() {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeCommandsObject(onChange),
    [store],
  );
  const getSnapshot = useCallback(() => store.getCommandsObject(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Alertas ENTER/EXIT de geocerca vivas (efímeras, para toasts). */
export function useFleetGeofenceAlerts() {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeGeofenceAlerts(onChange),
    [store],
  );
  const getSnapshot = useCallback(() => store.getGeofenceAlerts(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Versión de los campos de IDENTIDAD de la flota (grupo, placa, nombre,
 * conductor, tipo). Avanza con las ediciones administrativas y NO con la
 * telemetría.
 *
 * Lo usa quien deriva la estructura de la lista —agrupación, coincidencia de
 * búsqueda— para poder leer devices del store dentro de un `useMemo` y aun así
 * recalcular cuando esos campos cambian, sin despertarse con cada posición.
 */
export function useFleetMetadataVersion() {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeMetadata(onChange),
    [store],
  );
  const getSnapshot = useCallback(() => store.getMetadataVersion(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** IMEIs de la flota. Re-renderiza solo cuando entra o sale un vehículo. */
export function useFleetRoster() {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeRoster(onChange),
    [store],
  );
  const getSnapshot = useCallback(() => store.getRoster(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Contadores por estado de toda la flota (mantenidos incrementalmente). */
export function useFleetCounts() {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeCounts(onChange),
    [store],
  );
  const getSnapshot = useCallback(() => store.getCounts(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** `{ loading, wsConnected, isSocketOpen }` — estado agregado de la flota. */
export function useFleetStatus() {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeStatus(onChange),
    [store],
  );
  const getSnapshot = useCallback(() => store.getStatus(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Objetos de device para un subconjunto de IMEIs, con el ritmo de refresco
 * ESTRANGULADO.
 *
 * Es el puente para los consumidores que necesitan mirar muchos vehículos a la
 * vez pero no en tiempo real al milisegundo — hoy, la capa de clústeres: le
 * importa dónde están y si se mueven, y su propio recálculo ya está debounced
 * 150 ms más allá. Suscribirla al array vivo la despertaría con cada mensaje.
 *
 * Para pintar UN vehículo usa `useFleetDevice(imei)`, que sí es inmediato.
 *
 * @param {string[]} imeis
 * @param {object} [options]
 * @param {number} [options.throttleMs]
 */
export function useFleetDevicesFor(imeis, { throttleMs = 1000 } = {}) {
  const store = useFleetStore();
  const imeisRef = useRef(imeis);
  imeisRef.current = imeis;

  const read = useCallback(
    () => imeisRef.current.map((imei) => store.getDevice(imei)).filter(Boolean),
    [store],
  );

  const [devices, setDevices] = useState(read);
  const timerRef = useRef(null);
  const lastRunAtRef = useRef(0);

  // Un cambio en la lista de IMEIs es estructural (culling, filtro, alta o baja
  // de un vehículo): se aplica en el acto.
  useEffect(() => {
    lastRunAtRef.current = Date.now();
    setDevices(read());
  }, [imeis, read]);

  useEffect(() => {
    function schedule() {
      if (timerRef.current !== null) return;
      const elapsed = Date.now() - lastRunAtRef.current;
      const wait = elapsed >= throttleMs ? 0 : throttleMs - elapsed;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        lastRunAtRef.current = Date.now();
        setDevices(read());
      }, wait);
    }
    const off = store.subscribeDevicesArray(schedule);
    return () => {
      off();
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [store, read, throttleMs]);

  return devices;
}

/**
 * Array completo de la flota.
 *
 * ⚠️ Re-renderiza con cualquier cambio de cualquier vehículo. Úsalo solo en
 * pantallas que necesitan la lista entera y no conviven con el monitoreo
 * continuo. En el Monitor: `useFleetRoster()` + `useFleetDevice(imei)`.
 */
export function useFleetDevices() {
  const store = useFleetStore();
  const subscribe = useCallback(
    (onChange) => store.subscribeDevicesArray(onChange),
    [store],
  );
  const getSnapshot = useCallback(() => store.getDevicesArray(), [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
